import { useRef, useCallback } from 'react';
import {
  Flex,
  Box,
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogBackdrop,
  DialogPositioner,
  Portal,
} from '@chakra-ui/react';
import { LuDownload, LuX } from 'react-icons/lu';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

interface QrCodeModalProps {
  open: boolean;
  onClose: () => void;
  slug: string;
}

const QrCodeModal = ({ open, onClose, slug }: QrCodeModalProps) => {
  const { t } = useTranslation();
  const qrRef = useRef<HTMLDivElement>(null);
  const menuUrl = `${window.location.origin}/m/${slug}`;

  const handleDownloadPng = useCallback(() => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const size = 1024;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const link = document.createElement('a');
      link.download = `${slug}-qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }, [slug]);

  const handleDownloadSvg = useCallback(() => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const link = document.createElement('a');
    link.download = `${slug}-qr.svg`;
    link.href = URL.createObjectURL(blob);
    link.click();
    URL.revokeObjectURL(link.href);
  }, [slug]);

  return (
    <DialogRoot open={open} onOpenChange={(e) => !e.open && onClose()} placement="center">
      <Portal>
        <DialogBackdrop bg="blackAlpha.600" />
        <DialogPositioner
          display="flex"
          justifyContent={{ base: 'center', md: 'flex-end' }}
          alignItems={{ base: 'center', md: 'stretch' }}
        >
          <DialogContent
            backgroundColor="neutral.800"
            borderColor="neutral.700"
            borderWidth="0px"
            borderLeftWidth={{ base: '0px', md: '1px' }}
            borderRadius="0px"
            maxW={{ base: '100%', md: '480px' }}
            width="100%"
            height={{ base: '100vh', md: 'auto' }}
            maxH="100vh"
            m="0px"
            p={{ base: '20px', md: '24px' }}
            overflow="auto"
          >
            <DialogHeader pb="0">
              <DialogTitle fontSize="20px" fontWeight="500" color="neutral.100">
                {t('menus.qrCode', 'QR Code')}
              </DialogTitle>
              <Box
                as="button"
                position="absolute"
                top="20px"
                right="20px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="32px"
                height="32px"
                borderRadius="6px"
                cursor="pointer"
                color="neutral.400"
                backgroundColor="transparent"
                _hover={{ backgroundColor: 'neutral.700', color: 'neutral.200' }}
                onClick={onClose}
              >
                <LuX size={18} />
              </Box>
            </DialogHeader>

            <DialogBody py="24px">
              <Flex direction="column" alignItems="center" gap="20px">
                <Box
                  ref={qrRef}
                  p="16px"
                  backgroundColor="#FFFFFF"
                  borderRadius="8px"
                >
                  <QRCodeSVG
                    value={menuUrl}
                    size={256}
                    level="H"
                  />
                </Box>
                <Box
                  fontSize="12px"
                  fontWeight="400"
                  color="neutral.500"
                  textAlign="center"
                >
                  {menuUrl}
                </Box>
                <Flex gap="12px">
                  <Button
                    onClick={handleDownloadPng}
                    height="fit-content"
                    py="8px"
                    px="16px"
                    fontWeight="500"
                    fontSize="14px"
                    color="neutral.900"
                    backgroundColor="neutral.100"
                    _hover={{ backgroundColor: 'neutral.200' }}
                  >
                    <Flex alignItems="center" gap="6px">
                      <LuDownload style={{ width: 14, height: 14 }} />
                      PNG
                    </Flex>
                  </Button>
                  <Button
                    onClick={handleDownloadSvg}
                    height="fit-content"
                    py="8px"
                    px="16px"
                    fontWeight="500"
                    fontSize="14px"
                    color="neutral.300"
                    backgroundColor="transparent"
                    borderWidth="1px"
                    borderColor="neutral.600"
                    _hover={{ borderColor: 'neutral.500', backgroundColor: 'neutral.700' }}
                  >
                    <Flex alignItems="center" gap="6px">
                      <LuDownload style={{ width: 14, height: 14 }} />
                      SVG
                    </Flex>
                  </Button>
                </Flex>
              </Flex>
            </DialogBody>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </DialogRoot>
  );
};

export default QrCodeModal;
